import { useState } from "react";
import { Warning, ArrowsLeftRight, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Oct 17, 2026 (Randy) — "somehow it got connected to the Cosmic Equivalence
 * YouTube API" on his East Tactics silo, and nothing in the product said so.
 *
 * Switching hats between silos is a legitimate one-tap action, so the fix is
 * not to prevent it — it is to make the current pairing legible at all times
 * and to refuse to draft while it looks wrong. Matched state is a quiet grey
 * line; mismatched is an amber block that does not go away on its own.
 *
 * Nothing here guesses. The first channel connected to a (silo, slot) is
 * remembered as the pairing, so a divergence is a fact, not a name-similarity
 * heuristic.
 */

export function PairingBanner({ pairing, siloId, channelSlot, siloName, onResolved }) {
  const [working, setWorking] = useState(false);
  if (!pairing || !pairing.connected_channel_id) return null;

  const matched = pairing.state === "matched";
  const connected = pairing.connected_channel_name || pairing.connected_channel_id;
  const paired = pairing.paired_channel_name || pairing.paired_channel_id;

  if (matched || pairing.state === "unpinned") {
    return (
      <div
        className="flex items-center gap-1.5 text-[10px] text-[#6B6A68] mb-2"
        data-testid={`yt-pairing-ok-${channelSlot}`}
        data-state={pairing.state}
        title={`This silo is paired with the YouTube channel "${connected}". Replies draft and post as that channel.`}
      >
        <Check size={11} weight="bold" className="text-[#4A7C59] shrink-0" />
        <span className="truncate">
          {siloName ? <strong className="font-medium text-[#3A3A38]">{siloName}</strong> : "This silo"}
          {" "}is replying as{" "}
          <strong className="font-medium text-[#3A3A38]">{connected}</strong>
        </span>
      </div>
    );
  }

  const confirmPairing = async () => {
    setWorking(true);
    try {
      await api.post(`/youtube/integration/${siloId}/${channelSlot}/confirm-pairing`);
      toast.success(`This silo now replies as ${connected}.`);
      onResolved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the pairing.");
    } finally { setWorking(false); }
  };

  // We know exactly which channel to go back to, so switch straight to it
  // rather than making the creator hunt through the channel picker.
  const switchBack = async () => {
    setWorking(true);
    try {
      await api.post("/youtube/switch-channel", {
        silo_id: siloId,
        channel_slot: channelSlot,
        youtube_channel_id: pairing.paired_channel_id,
      });
      toast.success(`Back to ${paired}.`);
      onResolved?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
        || `Could not switch back to ${paired}. Connect that channel once and it will be instant after that.`
      );
    } finally { setWorking(false); }
  };

  return (
    <div
      className="rounded-md border border-[#E5B98A] bg-[#FDF3E6] p-2.5 mb-2.5"
      data-testid={`yt-pairing-mismatch-${channelSlot}`}
      role="alert"
    >
      <div className="flex items-start gap-2">
        <Warning size={15} weight="duotone" className="text-[#A2611F] shrink-0 mt-px" />
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] font-medium text-[#6B3F10] leading-snug">
            This silo is paired with {paired}, but {connected} is connected.
          </div>
          <div className="text-[10.5px] text-[#8A5A22] leading-snug mt-1">
            Drafting is held until this is resolved — otherwise replies would publish from
            the wrong channel. Automatic replies are paused for this silo too.
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <button
              onClick={switchBack}
              disabled={working || !pairing.paired_channel_id}
              className="text-[10.5px] inline-flex items-center gap-1 px-2 py-1 rounded border border-[#D4A368] bg-white text-[#6B3F10] hover:bg-[#FBEEDC] transition-colors disabled:opacity-50"
              data-testid={`yt-pairing-switch-${channelSlot}`}
            >
              <ArrowsLeftRight size={12} weight="bold" /> {working ? "Switching…" : `Switch back to ${paired}`}
            </button>
            <button
              onClick={confirmPairing}
              disabled={working}
              className="text-[10.5px] text-[#8A5A22] hover:text-[#6B3F10] hover:underline disabled:opacity-50"
              data-testid={`yt-pairing-confirm-${channelSlot}`}
            >
              {working ? "Saving…" : `No — ${connected} is correct for this silo`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PairingBanner;
